import { useState } from 'react'
import {
  UserPlus, GraduationCap, Briefcase, Target, ArrowRight,
  ChevronDown, Users, FileText, RefreshCw, Info,
  Phone, MapPin, ClipboardList, CheckCircle, AlertTriangle
} from 'lucide-react'
import AnchorLink from './AnchorLink'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-6" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const Step = ({ number, icon: Icon, title, children }) => (
  <div className="relative pl-14 py-2">
    <span
      aria-hidden="true"
      className="wiki-mono absolute left-0 top-0 text-6xl font-bold leading-none select-none text-light-border-primary dark:text-dark-bg-tertiary"
    >
      {number}
    </span>
    <div className="absolute left-10 top-2 bottom-2 w-0.5 bg-primary-500 rounded-full" />
    <div className="pt-1">
      <h5 className="wiki-heading text-base font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary-500 flex-shrink-0" />}
        {title}
      </h5>
      {children}
    </div>
  </div>
)

const InfoBox = ({ children, variant = 'default' }) => {
  const styles = {
    default: 'bg-light-bg-secondary dark:bg-dark-bg-secondary border-light-border-primary dark:border-dark-border-primary',
    tip: 'bg-primary-50/50 dark:bg-primary-900/10 border-primary-500/30',
    warning: 'bg-amber-50 dark:bg-amber-900/10 border-amber-300 dark:border-amber-700',
  }
  return (
    <div className={`mt-3 rounded-lg p-3 border ${styles[variant]}`}>
      {children}
    </div>
  )
}

const talentTypes = [
  {
    key: 'candidate',
    label: 'Candidate',
    icon: GraduationCap,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    desc: 'A person going through the hiring process — screening, interviews, offers.'
  },
  {
    key: 'employee',
    label: 'Employee',
    icon: Briefcase,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    desc: 'A hired person actively working in the company or on a project.'
  },
  {
    key: 'presale',
    label: 'Presale',
    icon: Target,
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    desc: 'An employee temporarily assigned to pre-sales activities or client proposals.'
  },
]

const conversions = [
  { from: 'Candidate', fromColor: 'text-blue-600', to: 'Employee', toColor: 'text-green-600', label: 'Hire as Employee', desc: 'When a candidate passes all stages and is hired.' },
  { from: 'Candidate', fromColor: 'text-blue-600', to: 'Presale', toColor: 'text-purple-600', label: 'Promote to Presale', desc: 'When a candidate is pulled directly into pre-sales work.' },
  { from: 'Employee', fromColor: 'text-green-600', to: 'Presale', toColor: 'text-purple-600', label: 'Promote to Presale', desc: 'When an employee is moved to pre-sales activities.' },
  { from: 'Employee', fromColor: 'text-green-600', to: 'Candidate', toColor: 'text-blue-600', label: 'Demote to Candidate', desc: 'When an employee returns to the candidate pool.' },
  { from: 'Presale', fromColor: 'text-purple-600', to: 'Employee', toColor: 'text-green-600', label: 'Move to Employee', desc: 'When a presale is converted back to an active employee.' },
  { from: 'Presale', fromColor: 'text-purple-600', to: 'Candidate', toColor: 'text-blue-600', label: 'Move to Candidate', desc: 'When a presale returns to the candidate stage.' },
]

const faqItems = [
  {
    key: 'intro-q1',
    question: 'Where do I start if I just got access?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>Check the sidebar navigation — what you can see depends on your permissions. Typically a new user starts with:</p>
        <ul className="space-y-1 ml-4">
          <li>• <strong>Candidates</strong> — main working area for recruiters</li>
          <li>• <strong>Job Applications</strong> — incoming applications from external sources</li>
          <li>• <strong>Add Talent</strong> (button on the Talents page or Dashboard) — create a new record manually</li>
        </ul>
        <p>If you see a blank list and cannot add anyone — check with your administrator that statuses are configured. See the <strong>Status System</strong> section below.</p>
      </div>
    )
  },
  {
    key: 'intro-q2',
    question: 'Can I add a talent without a status?',
    answer: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        No. Status is a required field when creating a talent. If the status dropdown is empty, the system has no statuses configured for that type yet — go to <strong>Statuses</strong> in the sidebar (or ask your admin) and make sure statuses are set up for Candidates/Employees/Presales.
      </p>
    )
  },
  {
    key: 'intro-q3',
    question: 'What\'s the difference between "Change Status" and "Convert Type"?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p><strong>Change Status</strong> — moves the talent within their current type's workflow. Example: Candidate goes from "Screening" → "Interview". The person stays a Candidate.</p>
        <p><strong>Convert Type</strong> — changes the person's role in the system entirely. Example: Candidate → Employee. This moves them to a different section and assigns them a new status from the new type's list.</p>
      </div>
    )
  },
  {
    key: 'intro-q4',
    question: 'I converted a candidate to employee — where did they go?',
    answer: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        They disappeared from the Candidates list and now appear in <strong>Employees</strong>. Open the Employees section in the sidebar to find them. Their profile, contacts, and history are fully preserved.
      </p>
    )
  },
  {
    key: 'intro-q5',
    question: 'How do I create a talent from a Job Application?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>Open <strong>Job Applications</strong> in the sidebar. Each application that has no linked talent shows three buttons at the end of its row:</p>
        <ul className="space-y-1 ml-4">
          <li>• <strong>→ Candidate</strong> — creates a candidate, pre-fills fields from the application</li>
          <li>• <strong>→ Employee</strong> — creates an employee directly</li>
          <li>• <strong>→ Presale</strong> — creates a presale directly</li>
        </ul>
        <p>After conversion the application row shows a "View Talent Profile" link instead of the buttons.</p>
      </div>
    )
  },
]

const IntroductionSection = () => {
  const [expandedBlocks, setExpandedBlocks] = useState({})
  const toggleBlock = (key) => setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }))

  return (
    <div className="space-y-10">

      {/* What is this system */}
      <div>
        <SubSectionHeading id="intro-overview">What is Talent Service?</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed mb-4">
          Talent Service is an internal CRM for managing people — candidates going through hiring,
          active employees, and presale specialists. All records are stored in one place with full
          history, contacts, statuses, CVs, and communications.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {talentTypes.map(t => {
            const Icon = t.icon
            return (
              <div key={t.key} className={`p-4 rounded-xl border ${t.border} ${t.bg}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${t.color}`} />
                  <span className={`wiki-heading text-sm font-semibold ${t.color}`}>{t.label}</span>
                </div>
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
                  {t.desc}
                </p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Adding a talent */}
      <div>
        <SubSectionHeading id="intro-add-talent">How to Add a New Candidate</SubSectionHeading>

        <div className="my-5 pl-4 border-l-2 border-primary-500/40 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg py-3 pr-4">
          <p className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-0.5">
            Before you start
          </p>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            Make sure statuses are configured for the type you need. If the "Current Status" dropdown is empty when creating a talent — see the <strong>Status System</strong> section below.
          </p>
        </div>

        <div className="space-y-8">
          <Step number="1" icon={UserPlus} title='Click "Add Talent" on the Talents page or Dashboard'>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
              The button is on the Talents list page and the Dashboard. It opens a full-page form.
            </p>
            <InfoBox variant="tip">
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                The form auto-saves a draft to localStorage — if you accidentally close the tab, your progress is preserved.
              </p>
            </InfoBox>
          </Step>

          <Step number="2" icon={ClipboardList} title="Fill in the required fields">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              The form has a left sidebar tracking completion. Red sections are required, yellow are partially filled, green are done.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { label: 'Last Name & First Name', required: true },
                { label: 'Talent Type', required: true, note: 'candidate / employee / presale' },
                { label: 'Current Status', required: true, note: 'depends on talent type' },
                { label: 'At least one Contact', required: true, note: 'phone, email, etc.' },
                { label: 'At least one Profession', required: true },
                { label: 'At least one Language', required: true },
                { label: 'Country & City', required: false },
                { label: 'Manager', required: false, note: 'defaults to you' },
              ].map(field => (
                <div key={field.label} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${field.required ? 'bg-primary-500' : 'bg-light-text-tertiary dark:bg-dark-text-tertiary'}`} />
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className={field.required ? 'font-medium text-light-text-primary dark:text-dark-text-primary' : ''}>
                      {field.label}
                    </span>
                    {field.note && <span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — {field.note}</span>}
                  </span>
                </div>
              ))}
            </div>
            <InfoBox>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                <strong className="text-light-text-primary dark:text-dark-text-primary">Red dot</strong> = required field&nbsp;&nbsp;·&nbsp;&nbsp;
                <strong className="text-light-text-primary dark:text-dark-text-primary">Grey dot</strong> = optional field
              </p>
            </InfoBox>
          </Step>

          <Step number="3" icon={CheckCircle} title='Click "Create"'>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              The submit button is at the bottom of the form. After saving you are redirected to the talent's detail page where you can continue filling in CVs, tools, notes, and more.
            </p>
          </Step>
        </div>

        <InfoBox variant="tip">
          <p className="text-xs font-medium text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-primary-500" /> AI Resume Parser
          </p>
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            On the CV section of the form there is an <strong>AI Resume Parser</strong> button. Upload a PDF resume and the system will automatically extract name, contacts, professions, and languages — saving manual data entry.
          </p>
        </InfoBox>
      </div>

      {/* Talent detail */}
      <div>
        <SubSectionHeading id="intro-detail">Talent Profile — What's Inside</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Clicking a talent's name opens their profile page. The left sidebar of the profile lists all sections — scroll through or click a section to jump to it.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { label: 'Basic Info', desc: 'Name, type, manager, location' },
            { label: 'Contacts', desc: 'Phone, email, messengers, socials' },
            { label: 'Professions', desc: 'Roles and departments' },
            { label: 'Tools & Skills', desc: 'Technologies, software' },
            { label: 'Languages', desc: 'With proficiency level' },
            { label: 'CVs', desc: 'Uploaded resume files' },
            { label: 'Notes', desc: 'Internal team notes' },
            { label: 'Communications', desc: 'Email/call history' },
            { label: 'Salary', desc: 'Expectations and offers' },
            { label: 'Important Dates', desc: 'Start date, birthday, etc.' },
            { label: 'Media', desc: 'Photos, intro videos' },
            { label: 'History', desc: 'Full event timeline' },
          ].map(s => (
            <div key={s.label} className="p-3 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
              <p className="text-xs font-semibold text-light-text-primary dark:text-dark-text-primary">{s.label}</p>
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Changing status */}
      <div>
        <SubSectionHeading id="intro-change-status">How to Change a Talent's Status</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Status reflects where the talent is in the current workflow (e.g. Screening → Interview → Offer → Hired). Changing it moves them to the next stage.
        </p>
        <div className="space-y-8">
          <Step number="1" icon={RefreshCw} title="Find the status badge">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              The current status is shown as a colored badge in the talent's row on the list page, and prominently in the header of the detail page.
            </p>
          </Step>
          <Step number="2" icon={ClipboardList} title="Click the edit icon next to the status">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
              A modal opens showing the allowed next statuses (configured via transitions). Only the transitions your admin has set up will appear.
            </p>
            <InfoBox variant="warning">
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                Some transitions require a <strong>reason</strong> — a text field will appear in the modal. This is controlled per-transition by your administrator.
              </p>
            </InfoBox>
          </Step>
          <Step number="3" icon={CheckCircle} title="Select the new status and confirm">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              Choose the target status, optionally enter a reason, and click Confirm. The status updates immediately and the change is logged in the History tab.
            </p>
          </Step>
        </div>
      </div>

      {/* Converting type */}
      <div>
        <SubSectionHeading id="intro-convert">How to Convert a Talent (Candidate → Employee, etc.)</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Converting a talent changes their type — moving them from one section of the system to another. All data is preserved.
        </p>

        <div className="space-y-8 mb-6">
          <Step number="1" icon={Users} title="Open the list for the talent's current type">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              Go to <strong>Candidates</strong>, <strong>Employees</strong>, or <strong>Presale</strong> in the sidebar — whichever section the talent currently belongs to.
            </p>
          </Step>
          <Step number="2" icon={ArrowRight} title="Click the conversion button on their row">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              Each row has type-conversion buttons at the end. They vary by the talent's current type:
            </p>
            <div className="space-y-2">
              {conversions.map(c => (
                <div key={`${c.from}-${c.to}`} className="flex items-center gap-3 text-xs p-2 rounded-lg bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary">
                  <span className={`font-semibold ${c.fromColor} dark:opacity-80 w-16 flex-shrink-0`}>{c.from}</span>
                  <ArrowRight className="w-3 h-3 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0" />
                  <span className={`font-semibold ${c.toColor} dark:opacity-80 w-16 flex-shrink-0`}>{c.to}</span>
                  <span className="text-light-text-tertiary dark:text-dark-text-tertiary">·</span>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">{c.desc}</span>
                </div>
              ))}
            </div>
          </Step>
          <Step number="3" icon={CheckCircle} title="Confirm — the talent moves to the new section">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              The talent disappears from the current list and appears in the target section (e.g. after hiring a candidate they appear in Employees). Their profile, history, and contacts remain intact.
            </p>
          </Step>
        </div>
      </div>

      {/* Job Applications */}
      <div>
        <SubSectionHeading id="intro-job-apps">Job Applications — Creating a Talent from an Application</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Job Applications are incoming requests from candidates (e.g. from a career page or external form). They are <em>not</em> talents yet — you decide whether to convert them.
        </p>
        <div className="space-y-8">
          <Step number="1" icon={FileText} title="Open Job Applications in the sidebar">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              The list shows all incoming applications. Applications without a linked talent profile show conversion buttons.
            </p>
          </Step>
          <Step number="2" icon={UserPlus} title="Click → Candidate, → Employee, or → Presale">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
              A talent creation form opens, pre-filled with data from the application — name, location, contacts, professions, and languages.
            </p>
            <InfoBox>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                Review and complete the form before submitting — the auto-fill is a starting point, not a guarantee of accuracy.
              </p>
            </InfoBox>
          </Step>
          <Step number="3" icon={CheckCircle} title="Submit — the application is linked to the new talent">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              After creation, the application row shows a <strong>"View Talent Profile"</strong> link instead of conversion buttons. The talent is now in the system and can be managed normally.
            </p>
          </Step>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <SubSectionHeading id="intro-faq">Common Questions</SubSectionHeading>
        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {faqItems.map(({ key, question, answer }) => (
            <div key={key} id={key} className="scroll-mt-6 group">
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    onClick={() => toggleBlock(key)}
                    className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors"
                  >
                    {question}
                  </button>
                  <AnchorLink id={key} />
                </div>
                <button onClick={() => toggleBlock(key)} className="flex-shrink-0 ml-4">
                  <ChevronDown
                    className={[
                      'w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200',
                      expandedBlocks[key] ? 'rotate-180' : ''
                    ].join(' ')}
                  />
                </button>
              </div>
              {expandedBlocks[key] && (
                <div className="pb-4 pr-6">{answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default IntroductionSection
